/**
 * useSpinnerAudio Hook
 * US-013: 轉盤轉動音效
 * US-014: 音效同步控制
 *
 * 專門處理轉盤音效的高階 hook，提供與動畫同步的音效控制
 */

import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useAudioPlayer } from './useAudioPlayer'

interface SpinnerAudioState {
  isSpinSoundLoaded: boolean
  isWinnerSoundLoaded: boolean
  isSpinSoundPlaying: boolean
  hasAudioError: boolean
}

interface SpinnerAudioControls {
  playSpinSound: () => Promise<void>
  stopSpinSound: () => void
  playWinnerSound: () => Promise<void>
  syncSpinSoundWithAnimation: (progress: number, isSpinning: boolean) => void
  setAudioVolume: (volume: number) => void
}

export interface UseSpinnerAudioReturn {
  audioState: SpinnerAudioState
  audioControls: SpinnerAudioControls
}

/**
 * 轉盤專用音效管理 hook
 */
export const useSpinnerAudio = (): UseSpinnerAudioReturn => {
  // 轉盤轉動音效
  const { audioState: spinAudioState, audioControls: spinAudioControls } =
    useAudioPlayer('/assets/Spin.mp3', {
      volume: 0.6,
      loop: false, // 修復：不循環播放，讓動畫時間與音效時間完全同步
      preload: true,
    })

  // 勝利音效
  const { audioState: winnerAudioState, audioControls: winnerAudioControls } =
    useAudioPlayer('/assets/Winner.mp3', {
      volume: 0.8,
      loop: false,
      preload: true,
    })

  // 記錄動畫狀態以便同步控制
  const animationStateRef = useRef({
    wasSpinning: false,
    lastProgress: 0,
  })

  // 轉盤音效播放 - US-013 AC-1, AC-2
  const playSpinSound = useCallback(async (): Promise<void> => {
    try {
      // 確保音效已載入再播放
      if (!spinAudioState.isLoaded && !spinAudioState.isError) {
        await spinAudioControls.load()
      }

      if (spinAudioState.isLoaded) {
        await spinAudioControls.play()
        console.log('SpinnerAudio: 轉盤音效開始播放')
      }
    } catch (error) {
      console.warn('SpinnerAudio: 轉盤音效播放失敗，但不影響動畫:', error)
      // US-013 AC-4: 音效失敗時靜默處理，不影響功能
    }
  }, [spinAudioState.isLoaded, spinAudioState.isError, spinAudioControls])

  // 停止轉盤音效 - US-014 AC-3
  const stopSpinSound = useCallback(() => {
    if (spinAudioState.isPlaying) {
      spinAudioControls.stop()
      console.log('SpinnerAudio: 轉盤音效停止')
    }
  }, [spinAudioState.isPlaying, spinAudioControls])

  // 勝利音效播放
  const playWinnerSound = useCallback(async (): Promise<void> => {
    try {
      if (!winnerAudioState.isLoaded && !winnerAudioState.isError) {
        await winnerAudioControls.load()
      }

      if (winnerAudioState.isLoaded) {
        await winnerAudioControls.play()
        console.log('SpinnerAudio: 勝利音效播放')
      }
    } catch (error) {
      console.warn('SpinnerAudio: 勝利音效播放失敗:', error)
    }
  }, [winnerAudioState.isLoaded, winnerAudioState.isError, winnerAudioControls])

  // 音效與動畫同步控制 - US-014 AC-1, AC-2, AC-3
  const syncSpinSoundWithAnimation = useCallback(
    (progress: number, isSpinning: boolean) => {
      const prevState = animationStateRef.current

      // US-014 AC-1: 動畫啟動時同步開始音效
      if (isSpinning && !prevState.wasSpinning) {
        // 動畫剛開始，啟動音效（延遲不超過50ms）
        playSpinSound()
      }

      // US-014 AC-3: 動畫停止時同步停止音效
      if (!isSpinning && prevState.wasSpinning) {
        // 動畫剛結束，停止音效（誤差不超過100ms）
        setTimeout(() => {
          stopSpinSound()
        }, 50) // 小延遲確保動畫完全停止
      }

      // US-014 AC-2: 根據動畫進度調整音效速度
      if (isSpinning && spinAudioState.isLoaded && spinAudioState.isPlaying) {
        // 修復：配合更長的動畫時間（6.8-7.2秒）與音效時間（7.026秒）同步
        // progress 0-1，速度從 1.0 平滑減到 0.3，確保音效與動畫完美配合
        const speedMultiplier = 1.0 - progress * 0.7
        const playbackRate = Math.max(0.3, speedMultiplier)

        spinAudioControls.setPlaybackRate(playbackRate)
      }

      // 更新狀態記錄
      animationStateRef.current = {
        wasSpinning: isSpinning,
        lastProgress: progress,
      }
    },
    [
      playSpinSound,
      stopSpinSound,
      spinAudioState.isLoaded,
      spinAudioState.isPlaying,
      spinAudioControls,
    ]
  )

  // 音量控制 - US-013 AC-3
  const setAudioVolume = useCallback(
    (volume: number) => {
      const clampedVolume = Math.max(0, Math.min(1, volume))
      spinAudioControls.setVolume(clampedVolume)
      winnerAudioControls.setVolume(clampedVolume)
    },
    [spinAudioControls, winnerAudioControls]
  )

  // 清理函數，確保音效正常停止
  useEffect(() => {
    return () => {
      if (spinAudioState.isPlaying) {
        spinAudioControls.stop()
      }
    }
  }, [spinAudioState.isPlaying, spinAudioControls])

  // 組合音效狀態
  const audioState: SpinnerAudioState = {
    isSpinSoundLoaded: spinAudioState.isLoaded,
    isWinnerSoundLoaded: winnerAudioState.isLoaded,
    isSpinSoundPlaying: spinAudioState.isPlaying,
    hasAudioError: spinAudioState.isError || winnerAudioState.isError,
  }

  // 修復：使用 useMemo 確保 audioControls 物件穩定，避免不必要的重新建立
  const audioControls: SpinnerAudioControls = useMemo(
    () => ({
      playSpinSound,
      stopSpinSound,
      playWinnerSound,
      syncSpinSoundWithAnimation,
      setAudioVolume,
    }),
    [
      playSpinSound,
      stopSpinSound,
      playWinnerSound,
      syncSpinSoundWithAnimation,
      setAudioVolume,
    ]
  )

  return {
    audioState,
    audioControls,
  }
}
